import { useState, useMemo, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getAllDailyCommitments, getAllMonthlyCommitments, getTodayDate, DailyCommitment, MonthlyCommitment } from "@/services/commitmentService";
import { getAllActiveUsers } from "@/services/userService";
import { userHasPermission } from "@/services/roleService";
import { useLeads } from "@/hooks/useLeads";
import { useAuth } from "@/contexts/AuthContext";
import { ApprovedUser, Lead, Permission } from "@/types";
import { Trophy, Share2, Medal, Award, ChevronLeft, ChevronRight } from "lucide-react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { format, subMonths, startOfMonth, endOfMonth, isSameMonth, isWithinInterval } from "date-fns";

function formatCurrency(amount: number) {
  if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
  if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
  return `₹${amount.toLocaleString("en-IN")}`;
}

// Format currency with unit based on value
function formatCurrencyWithUnit(amount: number) {
  if (amount >= 10000000) {
    return { value: (amount / 10000000).toFixed(2), unit: 'Cr' };
  }
  return { value: (amount / 100000).toFixed(2), unit: 'L' };
}

// Format display with target and unit
function formatValueWithTarget(current: number, target: number) {
  // Show dash for unavailable values
  const currentDisplay = current > 0 ? current : '-';
  const targetDisplay = target > 0 ? target : '-';
  
  // If both are dashes, just return -/-
  if (currentDisplay === '-' && targetDisplay === '-') {
    return '-/-';
  }
  
  // If target is not available, show current/- or -/- format
  if (target === 0 || target === undefined) {
    if (current > 0) {
      const { value, unit } = formatCurrencyWithUnit(current);
      return `₹${value}/-`;
    }
    return '-/-';
  }
  
  // If target > 100 lakh, show everything in crore
  if (target > 10000000) {
    const currentCr = current > 0 ? (current / 10000000).toFixed(2) : '-';
    const targetCr = (target / 10000000).toFixed(2);
    return `₹${currentCr}/${targetCr} Cr`;
  }
  
  // Otherwise show in lakh
  const currentL = current > 0 ? (current / 100000).toFixed(2) : '-';
  const targetL = (target / 100000).toFixed(2);
  return `₹${currentL}/${targetL} L`;
}

// Format count with target
function formatCountWithTarget(current: number, target: number) {
  const currentDisplay = current > 0 ? current : '-';
  const targetDisplay = target > 0 ? target : '-';
  return `${currentDisplay}/${targetDisplay}`;
}

const rankIcons = [
  <Trophy key="1" className="h-4 w-4 text-yellow-500" />,
  <Medal key="2" className="h-4 w-4 text-gray-400" />,
  <Award key="3" className="h-4 w-4 text-amber-600" />,
];

type Period = "today" | "month";

// Generate month options for last 12 months
function getMonthOptions() {
  const months = [];
  for (let i = 0; i <= 11; i++) {
    const date = subMonths(new Date(), i);
    months.push({
      value: format(date, 'yyyy-MM'),
      label: format(date, 'MMM yyyy'),
    });
  }
  return months;
}

export function Leaderboard() {
  const { leads: allLeads } = useLeads();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<"login" | "disbursed">("login");
  const [period, setPeriod] = useState<Period>("month");
  const [selectedMonth, setSelectedMonth] = useState<string>(format(new Date(), 'yyyy-MM'));
  const [dailyCommitments, setDailyCommitments] = useState<DailyCommitment[]>([]);
  const [monthlyCommitments, setMonthlyCommitments] = useState<MonthlyCommitment[]>([]);
  const [users, setUsers] = useState<ApprovedUser[]>([]);
  const [allActiveUsers, setAllActiveUsers] = useState<ApprovedUser[]>([]);
  const [loadingCommitments, setLoadingCommitments] = useState(true);
  
  const monthOptions = useMemo(() => getMonthOptions(), []);

  // Load users from Firebase
  useEffect(() => {
    const loadUsers = async () => {
      const activeUsers = await getAllActiveUsers();
      setAllActiveUsers(activeUsers); // Store all users for Direct entry lookup
      
      // Filter users to only include:
      // 1. Pure sales_agent role (must have sales_agent role)
      // 2. Must NOT have operations_user or admin roles
      // 3. Must have view_leaderboard permission
      const filteredUsers = await Promise.all(
        activeUsers.map(async (user) => {
          const hasPermission = await userHasPermission(user.roles, Permission.VIEW_LEADERBOARD);
          const isSalesAgent = user.roles.includes('sales_agent');
          const isAdmin = user.roles.includes('admin');
          const isOperations = user.roles.includes('operations_user');
          
          // Only include pure sales agents (not admin or operations)
          return (hasPermission && isSalesAgent && !isAdmin && !isOperations) ? user : null;
        })
      );
      setUsers(filteredUsers.filter((user): user is ApprovedUser => user !== null));
    };
    loadUsers();
  }, []);

  // Load commitments from Firebase
  useEffect(() => {
    const loadCommitments = async () => {
      setLoadingCommitments(true);
      try {
        if (period === "today") {
          // Load daily commitments for today
          const todayDate = getTodayDate();
          const daily = await getAllDailyCommitments(todayDate);
          setDailyCommitments(daily);
          setMonthlyCommitments([]); // Clear monthly when viewing daily
        } else {
          // Load monthly commitments for selected month
          const monthly = await getAllMonthlyCommitments(selectedMonth);
          setMonthlyCommitments(monthly);
          setDailyCommitments([]); // Clear daily when viewing monthly
        }
      } catch (error) {
        console.error('Error loading commitments:', error);
        toast.error('Failed to load commitment data');
      } finally {
        setLoadingCommitments(false);
      }
    };

    loadCommitments();
  }, [period, selectedMonth]);

  // Calculate leaderboard data from leads
  const leaderboardData = useMemo(() => {
    const selectedDate = new Date(selectedMonth + '-01');
    const monthStart = startOfMonth(selectedDate);
    const monthEnd = endOfMonth(selectedDate);
    
    // Find Anurag Mishra for Direct entry targets (from all users, not just filtered sales agents)
    // Normalize phone: try last 10 digits to handle various stored formats (+919415094980, 9415094980, 09415094980)
    const anuragMishra = allActiveUsers.find(u => {
      const normalized = (u.phone || '').replace(/\D/g, '');
      return normalized.endsWith('9415094980');
    });
    
    if (activeTab === "login") {
      // Login leaderboard – based on loginRecords added within the period
      // Each LoginRecord in lead.loginRecords is one "file" (one application filed)
      const loginsByUser: Record<string, { amount: number; count: number }> = {};
      
      allLeads.forEach(lead => {
        const records = lead.loginRecords || [];
        if (records.length === 0) return;

        // Prefer the record explicitly marked as main;
        // fall back to earliest by date for legacy records without isMain flag
        const toDate = (d: unknown): Date => {
          if (d instanceof Date) return d;
          if (d && typeof (d as { toDate?: () => Date }).toDate === 'function') return (d as { toDate: () => Date }).toDate();
          return new Date(d as string | number);
        };

        const mainRecord = records.find(r => r.isMain)
          ?? records.reduce((earliest, r) => {
            return toDate(r.createdAt) < toDate(earliest.createdAt) ? r : earliest;
          });

        const recordDate = toDate(mainRecord.createdAt);
        const matchesPeriod = period === "today"
          ? format(recordDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd')
          : isWithinInterval(recordDate, { start: monthStart, end: monthEnd });

        if (matchesPeriod) {
          // Attribute to the lead OWNER (createdBy), not whoever entered the record
          const userId = lead.createdBy || lead.assignedTo;
          if (!loginsByUser[userId]) {
            loginsByUser[userId] = { amount: 0, count: 0 };
          }
          loginsByUser[userId].amount += mainRecord.loanAmount || 0;
          loginsByUser[userId].count += 1;
        }
      });

      // Create entries from sales agents
      const salesAgentEntries = users.map((user, index) => {
        const userData = loginsByUser[user.userId] || { amount: 0, count: 0 };
        
        // Get user's commitment from Firebase
        let userCommitment;
        if (period === "today") {
          userCommitment = dailyCommitments.find(c => c.userId === user.userId);
        } else {
          userCommitment = monthlyCommitments.find(c => c.userId === user.userId);
        }
        
        return {
          rank: index + 1,
          userId: user.userId,
          name: user.name,
          amount: userData.amount || 0,
          count: userData.count || 0,
          target: userCommitment?.loginAmount || 0,
          targetCount: userCommitment?.loginFiles || 0,
        };
      });
      
      // Calculate Direct entry (all users NOT in the sales agent leaderboard)
      // This includes Anurag Mishra and ANY other user with any role not listed as individual sales agent
      const salesAgentIds = new Set(users.map(u => u.userId));
      let directAmount = 0;
      let directCount = 0;
      
      // Aggregate achievements from all non-sales-agent users
      Object.entries(loginsByUser).forEach(([userId, data]) => {
        if (!salesAgentIds.has(userId)) {
          directAmount += data.amount;
          directCount += data.count;
        }
      });
      
      // Get Anurag Mishra's commitment for Direct target
      let directCommitment;
      if (anuragMishra) {
        if (period === "today") {
          directCommitment = dailyCommitments.find(c => c.userId === anuragMishra.userId);
        } else {
          directCommitment = monthlyCommitments.find(c => c.userId === anuragMishra.userId);
        }
      }
      
      // Add Direct entry
      const directEntry = {
        rank: 0,
        userId: 'direct',
        name: 'Direct',
        amount: directAmount,
        count: directCount,
        target: directCommitment?.loginAmount || 0,
        targetCount: directCommitment?.loginFiles || 0,
      };
      
      const allEntries = [directEntry, ...salesAgentEntries];
      return allEntries.sort((a, b) => b.amount - a.amount).map((e, i) => ({ ...e, rank: i + 1 }));
    } else {
      // Disbursement leaderboard - based on salesConsiderationDate or disbursement date
      // Handle both full disbursements and partial disbursements
      const disbursementsByUser: Record<string, { amount: number; count: number }> = {};
      
      allLeads.forEach(lead => {
        // Check if lead has disbursement records (partial or full)
        if (lead.disbursements && lead.disbursements.length > 0) {
          // Count file as 1 per lead, sum all disbursement amounts in the period
          const userId = lead.createdBy || lead.assignedTo;
          let leadPeriodAmount = 0;
          let hasMatchingDisbursement = false;
          
          lead.disbursements.forEach(disbursement => {
            const considerationDate = disbursement.salesConsiderationDate 
              ? new Date(disbursement.salesConsiderationDate)
              : disbursement.disbursementDate;
              
            const matchesPeriod = period === "today"
              ? format(considerationDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd')
              : isWithinInterval(considerationDate, { start: monthStart, end: monthEnd });
              
            if (matchesPeriod) {
              leadPeriodAmount += disbursement.disbursedAmount;
              hasMatchingDisbursement = true;
            }
          });
          
          if (hasMatchingDisbursement) {
            if (!disbursementsByUser[userId]) {
              disbursementsByUser[userId] = { amount: 0, count: 0 };
            }
            disbursementsByUser[userId].amount += leadPeriodAmount;
            disbursementsByUser[userId].count += 1; // 1 file per lead
          }
        } else if (lead.status === 'disbursed' && lead.disbursedAmount) {
          // Legacy: Handle old disbursements without disbursement records
          const considerationDate = lead.salesConsiderationDate 
            ? new Date(lead.salesConsiderationDate)
            : lead.updatedAt;
            
          const matchesPeriod = period === "today"
            ? format(considerationDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd')
            : isWithinInterval(considerationDate, { start: monthStart, end: monthEnd });
            
          if (matchesPeriod) {
            // Attribute to lead OWNER (createdBy), not assignedTo
            const userId = lead.createdBy || lead.assignedTo;
            if (!disbursementsByUser[userId]) {
              disbursementsByUser[userId] = { amount: 0, count: 0 };
            }
            disbursementsByUser[userId].amount += lead.disbursedAmount;
            disbursementsByUser[userId].count += 1;
          }
        }
      });

      // Create entries from sales agents
      const salesAgentEntries = users.map((user, index) => {
        // Get user's commitment from Firebase
        let userCommitment;
        if (period === "today") {
          userCommitment = dailyCommitments.find(c => c.userId === user.userId);
        } else {
          userCommitment = monthlyCommitments.find(c => c.userId === user.userId);
        }
        
        return {
          rank: index + 1,
          userId: user.userId,
          name: user.name,
          amount: disbursementsByUser[user.userId]?.amount || 0,
          count: disbursementsByUser[user.userId]?.count || 0,
          target: userCommitment?.disbursementAmount || 0,
          targetCount: userCommitment?.disbursementFiles || 0,
        };
      });
      
      // Calculate Direct entry (all users NOT in the sales agent leaderboard)
      // This includes Anurag Mishra and ANY other user with any role not listed as individual sales agent
      const salesAgentIds = new Set(users.map(u => u.userId));
      let directAmount = 0;
      let directCount = 0;
      
      // Aggregate achievements from all non-sales-agent users
      Object.entries(disbursementsByUser).forEach(([userId, data]) => {
        if (!salesAgentIds.has(userId)) {
          directAmount += data.amount;
          directCount += data.count;
        }
      });
      
      // Get Anurag Mishra's commitment for Direct target
      let directCommitment;
      if (anuragMishra) {
        if (period === "today") {
          directCommitment = dailyCommitments.find(c => c.userId === anuragMishra.userId);
        } else {
          directCommitment = monthlyCommitments.find(c => c.userId === anuragMishra.userId);
        }
      }
      
      // Add Direct entry
      const directEntry = {
        rank: 0,
        userId: 'direct',
        name: 'Direct',
        amount: directAmount,
        count: directCount,
        target: directCommitment?.disbursementAmount || 0,
        targetCount: directCommitment?.disbursementFiles || 0,
      };
      
      const allEntries = [directEntry, ...salesAgentEntries];
      return allEntries.sort((a, b) => b.amount - a.amount).map((e, i) => ({ ...e, rank: i + 1 }));
    }
  }, [activeTab, period, selectedMonth, dailyCommitments, monthlyCommitments, users, allActiveUsers, allLeads]);

  const handleShare = () => {
    const title = activeTab === "login" ? "Login Leaderboard" : "Disbursement Leaderboard";
    const periodLabel = period === "today" ? "Today" : monthOptions.find(m => m.value === selectedMonth)?.label || "This Month";
    
    let message = `🏆 *${title} - ${periodLabel}*\n\n`;
    leaderboardData.forEach((entry, index) => {
      const medal = index === 0 ? "🥇" : index === 1 ? "🥈" : index === 2 ? "🥉" : `${index + 1}.`;
      const targetCountStr = entry.targetCount > 0 ? `${entry.targetCount}` : '-';
      const filesLine = `${entry.count > 0 ? entry.count : '-'} / ${targetCountStr} files`;
      const targetAmountStr = entry.target > 0 ? formatCurrency(entry.target) : '-';
      const amountLine = `${entry.amount > 0 ? formatCurrency(entry.amount) : '-'} / ${targetAmountStr}`;
      message += `${medal} ${entry.name}\n   • ${filesLine}\n   • ${amountLine}\n\n`;
    });

    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    toast.success("Opening WhatsApp to share leaderboard");
  };

  const handleMonthChange = (direction: 'prev' | 'next') => {
    const currentIndex = monthOptions.findIndex(m => m.value === selectedMonth);
    if (direction === 'prev' && currentIndex < monthOptions.length - 1) {
      setSelectedMonth(monthOptions[currentIndex + 1].value);
    } else if (direction === 'next' && currentIndex > 0) {
      setSelectedMonth(monthOptions[currentIndex - 1].value);
    }
  };

  return (
    <Card variant="elevated" className="overflow-hidden w-full min-w-0 flex flex-col h-[600px]">
      <CardHeader className="pb-2 flex-shrink-0">
        <div className="flex items-center justify-between gap-1">
          <div className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-warning" />
            <CardTitle className="text-base md:text-lg">Leaderboard</CardTitle>
          </div>
          <div className="flex items-center gap-1">
            {/* Period Toggle */}
            <Tabs value={period} onValueChange={(v) => setPeriod(v as Period)}>
              <TabsList className="h-8 bg-secondary/50">
                <TabsTrigger value="today" className="text-xs h-6 px-2 data-[state=active]:bg-success data-[state=active]:text-success-foreground">
                  Today
                </TabsTrigger>
                <TabsTrigger value="month" className="text-xs h-6 px-2 data-[state=active]:bg-success data-[state=active]:text-success-foreground">
                  Month
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <Button variant="ghost" size="icon" onClick={handleShare} className="h-8 w-8">
              <Share2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 flex-shrink-0">
        {/* Month Selector - Horizontal Scroll on Mobile */}
        {period === "month" && (
          <div className="flex items-center justify-between gap-2 bg-secondary/30 rounded-lg p-2">
            {/* Desktop: Full selector */}
            <div className="hidden md:flex items-center gap-2 flex-1">
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-7 w-7"
                onClick={() => handleMonthChange('prev')}
                disabled={monthOptions.findIndex(m => m.value === selectedMonth) >= monthOptions.length - 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger className="h-7 w-auto border-0 bg-transparent text-sm font-medium">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {monthOptions.map(month => (
                    <SelectItem key={month.value} value={month.value}>
                      {month.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-7 w-7"
                onClick={() => handleMonthChange('next')}
                disabled={monthOptions.findIndex(m => m.value === selectedMonth) <= 0}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            
            {/* Mobile: Compact buttons */}
            <div className="md:hidden flex items-center gap-1 flex-1 justify-center">
              <Button 
                variant="ghost" 
                size="sm"
                className="h-7 px-2"
                onClick={() => handleMonthChange('prev')}
                disabled={monthOptions.findIndex(m => m.value === selectedMonth) >= monthOptions.length - 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-xs font-medium px-2">
                {monthOptions.find(m => m.value === selectedMonth)?.label}
              </span>
              <Button 
                variant="ghost" 
                size="sm"
                className="h-7 px-2"
                onClick={() => handleMonthChange('next')}
                disabled={monthOptions.findIndex(m => m.value === selectedMonth) <= 0}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Type Tabs */}
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "login" | "disbursed")}>
          <TabsList className="grid w-full grid-cols-2 h-9 bg-secondary/50">
            <TabsTrigger value="login" className="text-xs data-[state=active]:bg-accent data-[state=active]:text-accent-foreground">
              Login
            </TabsTrigger>
            <TabsTrigger value="disbursed" className="text-xs data-[state=active]:bg-success data-[state=active]:text-success-foreground">
              Disbursed
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Leaderboard List */}
        <div className="overflow-y-auto space-y-2 pr-1" style={{ maxHeight: 'calc(600px - 200px)' }}>
          {leaderboardData.map((entry, index) => {
            const isCurrentUser = entry.userId === user?.id;
            
            return (
              <motion.div
                key={entry.rank}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className={cn(
                  "flex items-center gap-3 rounded-lg p-2.5 transition-colors",
                  isCurrentUser && "bg-accent/10 border border-accent/20",
                  index === 0 && !isCurrentUser && "bg-warning/10",
                  index === 1 && !isCurrentUser && "bg-secondary/50",
                  index === 2 && !isCurrentUser && "bg-amber-500/10",
                  index > 2 && !isCurrentUser && "bg-secondary/30"
                )}
              >
                <div className="flex items-center justify-center w-7 h-7">
                  {index < 3 ? rankIcons[index] : (
                    <span className="text-sm font-semibold text-muted-foreground">{entry.rank}</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={cn(
                    "text-sm font-medium truncate",
                    isCurrentUser && "text-accent"
                  )}>
                    {entry.name}
                    {isCurrentUser && <span className="text-xs ml-1 opacity-70">(You)</span>}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatValueWithTarget(entry.amount, entry.target)} • {formatCountWithTarget(entry.count, entry.targetCount)}
                  </p>
                </div>
                <Badge 
                  variant={isCurrentUser ? "accent" : index === 0 ? "warning" : "secondary"} 
                  className="text-[10px] px-1.5"
                >
                  {entry.target > 0 ? Math.round((entry.amount / entry.target) * 100) : 0}%
                </Badge>
              </motion.div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

